import * as React from 'react';

import classnames from 'classnames';
import * as ramda from 'ramda';

import type {ColorName} from '../colors/colors';
import {NothingFoundEmptyState} from '../empty-states/nothing-found-empty-state';
import {ErrorState} from '../error-state/error-state';
import type {StackSpacing} from '../layout/stack/stack-legacy';
import {LoadingPage} from '../loading-page/loading-page';
import {measureScrollbarWidth} from '../utils/scrollbar';

import {renderSpecialState} from './helpers';
import {ListViewLoadingBars} from './list-view-loading-bars';

import styles from './list-view.module.css';

export type SpecialState = {
    shouldRender: () => boolean;
    component: React.ReactNode;
    centerVertically?: boolean;
};

export type ListViewProps = {
    collection: any[];
    renderRow: (item: any, index: number) => React.ReactElement;
    itemSpacing?: 8 | 16;
    listPadding?: 8 | 16;
    specialStates?: SpecialState[];
    isLoading?: boolean;
    /**
     * Use to customize or override the default loading state rendered by this component, by default, if
     * this prop is undefined, the built-in loading state will be rendered whenever isLoading is true
     */
    loadingState?: {
        // customize values for built-in loading state
        itemHeight?: number; // height of loading state list item,
        spacing?: StackSpacing; // spacing between list items,
        numItems?: number; // number of items to appear in loading state,
        listPadding?: number; // padding around loading state list,
        backgroundColor?: ColorName; // background color of loading state list,
        // display custom component instead of built-in loading state
        customComponent?: React.ReactNode;
    };
    /**
     * Use to display a loading overlay when the list is being updated. Differs from isLoading in that
     * the underlying list items are still visible. Requires the parent component to have a relative position.
     */
    isUpdating?: boolean;
    shouldScrollToTopWhenUpdating?: boolean;
    isError?: boolean;
    /**
     * Use to customize or override the default error state rendered by this component, by default, if
     * this prop is undefined, the built-in error state will be rendered whenever isError is true
     */
    errorState?: {
        // customize values for built-in error state
        errorMessage?: string | null | undefined;
        size?: 'big' | 'small';
        // display custom component instead of built-in error state
        customComponent?: React.ReactNode;
    };
    isFiltering?: boolean;
    /**
     * Use to customize or override the default nothing found state rendered by this component, by default, if
     * this prop is undefined, the built-in nothing found state will be rendered when the following conditions are met:
     *   isLoading: false
     *   isError: false
     *   isFiltering: true
     *   collection.length === 0
     */
    nothingFoundState?: {
        title?: string;
        subtitle?: string;
    };
    /**
     * Component to render under the following conditions:
     *   isLoading: false
     *   isError: false
     *   isFiltering: false
     *   collection.length === 0
     */
    emptyStateComponent?: React.ReactNode;
    isTable?: boolean;
    shouldfadeBottom?: boolean;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    getListViewRef?: (arg1?: HTMLElement | null | undefined) => void;
    isFullWidth?: boolean;
    isFullHeight?: boolean;
    isOverflowAuto?: boolean;
};

const findSpecialState = ramda.find((state: SpecialState) => {
    return state.shouldRender();
});

/**
This is a generic, building-block component used to render a list of
items.  An array of items is specified as a `collection`, and each
item in the array is mapped into a `renderRow` function.  Additionally,
header and footer elements can also be provided, if desired, and they
will be added to the start and end of the list, respectively.

Finally, an array of `specialStates` can be provided.  These must
be objects with both a `shouldRender` function that returns true
or false, and a `component` prop which is a react element to display
if the `shouldRender` evaluates to `true`.
 */
export function ListView(props: ListViewProps) {
    const {
        collection = [],
        specialStates = [],
        getListViewRef,
        isUpdating,
        shouldScrollToTopWhenUpdating,
    } = props;

    const listViewRef = React.useRef(null);

    const scrollbarWidth: number = measureScrollbarWidth();

    const Node = props.isTable ? 'table' : 'div';

    let specialState;
    if (props.isLoading && collection.length === 0) {
        // only want to show this if it might be the initial load
        specialState = {
            shouldRender: () => true,
            component:
                props.loadingState && props.loadingState.customComponent ? (
                    props.loadingState.customComponent
                ) : (
                    <ListViewLoadingBars
                        barHeight={props.loadingState ? props.loadingState.itemHeight : undefined}
                        spacing={props.loadingState ? props.loadingState.spacing : undefined}
                        numBars={props.loadingState ? props.loadingState.numItems : undefined}
                        padding={props.loadingState ? props.loadingState.listPadding : undefined}
                        backgroundColor={
                            props.loadingState ? props.loadingState.backgroundColor : undefined
                        }
                    />
                ),
        };
    } else if (props.isError) {
        specialState = {
            shouldRender: () => true,
            component:
                props.errorState && props.errorState.customComponent ? (
                    props.errorState.customComponent
                ) : (
                    <ErrorState
                        errorMessage={props.errorState ? props.errorState.errorMessage : undefined}
                        size={props.errorState ? props.errorState.size : undefined}
                    />
                ),
        };
    } else if (props.isFiltering && !props.isLoading && !props.isError && !collection.length) {
        specialState = {
            shouldRender: () => true,
            component: (
                <NothingFoundEmptyState
                    title={props.nothingFoundState ? props.nothingFoundState.title : undefined}
                    subtitle={
                        props.nothingFoundState ? props.nothingFoundState.subtitle : undefined
                    }
                />
            ),
        };
    } else if (
        props.emptyStateComponent &&
        !props.isFiltering &&
        !props.isLoading &&
        !props.isError &&
        !collection.length
    ) {
        specialState = {component: props.emptyStateComponent, shouldRender: () => true};
    } else {
        // The above common special states should not exist in specialStates list
        specialState = findSpecialState(specialStates);
    }

    const header = props.isTable ? <thead>{props.header}</thead> : props.header;

    // @ts-expect-error
    const getList = (renderRow, itemSpacing) => {
        if (props.isTable) {
            return <tbody>{collection.map(renderRow)}</tbody>;
        }

        return (
            <ul className={props.listPadding ? `pad-${props.listPadding}` : undefined}>
                {collection.map((row, index) => {
                    return (
                        <li
                            key={index}
                            className={
                                itemSpacing && index !== props.collection.length - 1
                                    ? `mb-${itemSpacing}`
                                    : undefined
                            }
                        >
                            {renderRow(row, index)}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const footer = props.isTable ? <tfoot>{props.footer}</tfoot> : props.footer;

    const styleNames = classnames({
        [styles['list-view-loading']]: props.isLoading,
        [styles['list-view-loading--no-background-color']]:
            props.isLoading && props.loadingState && props.loadingState.backgroundColor,
        [styles['fade-full']]: props.shouldfadeBottom && !scrollbarWidth,
        [styles['fade-with-scrollbar']]: props.shouldfadeBottom && scrollbarWidth,
        [styles['full-width']]: props.isFullWidth,
        [styles['full-height']]:
            (specialState && specialState.centerVertically) || props.isFullHeight,
        [styles['overflow-auto']]: props.isOverflowAuto,
    });

    React.useLayoutEffect(() => {
        if (listViewRef.current && getListViewRef) {
            getListViewRef(listViewRef.current);
        }
    }, [getListViewRef]);

    React.useLayoutEffect(() => {
        if (listViewRef.current && isUpdating && shouldScrollToTopWhenUpdating) {
            // @ts-expect-error
            listViewRef.current.scrollTop = 0;
        }
    }, [isUpdating, shouldScrollToTopWhenUpdating]);

    return (
        <Node ref={listViewRef} className={styleNames}>
            {props.header ? header : null}
            {specialState
                ? renderSpecialState(specialState)
                : getList(props.renderRow, props.itemSpacing)}
            {props.footer ? footer : null}
            {props.isUpdating && collection.length > 0 && (
                <div className={classnames('full-height full-width', styles['updating-overlay'])}>
                    <div className='mt-64'>
                        <LoadingPage />
                    </div>
                </div>
            )}
        </Node>
    );
}
